import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { SkillDetail } from '../../types/team.js';
import * as teamApi from '../../services/team-api.js';

type LoadState = 'idle' | 'loading' | 'loaded' | 'error';

@customElement('cp-team-edit-modal')
export class CpTeamEditModal extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Boolean }) open = false;
  @property({ type: String }) skillId = '';
  @property({ type: Array }) availableMcpTools: string[] = [];

  @state() private _loadState: LoadState = 'idle';
  @state() private _skillDetail: SkillDetail | null = null;
  @state() private _errorMessage = '';
  @state() private _isSaving = false;
  @state() private _currentContent = '';
  @state() private _selectedMcpTools: string[] = [];

  private _lastLoadedSkillId = '';

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this._onKeyDown);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onKeyDown);
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('open') && this.open && this.skillId) {
      if (this.skillId !== this._lastLoadedSkillId) {
        this._loadSkillDetail();
      }
    }

    if (changed.has('open') && !this.open) {
      this._lastLoadedSkillId = '';
      this._loadState = 'idle';
      this._skillDetail = null;
      this._errorMessage = '';
      this._isSaving = false;
      this._currentContent = '';
      this._selectedMcpTools = [];
    }
  }

  private _onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.open) {
      this._close();
    }
  };

  private _close(): void {
    this.dispatchEvent(new CustomEvent('modal-closed', { bubbles: true, composed: true }));
  }

  private _onBackdropClick(e: Event): void {
    if (e.target === e.currentTarget) {
      this._close();
    }
  }

  private async _loadSkillDetail(): Promise<void> {
    if (!this.skillId) return;
    this._loadState = 'loading';
    this._lastLoadedSkillId = this.skillId;

    try {
      this._skillDetail = await teamApi.getSkillDetail(this.skillId);
      this._currentContent = this._skillDetail.skillContent;
      this._selectedMcpTools = [...(this._skillDetail.mcpTools || [])];
      this._loadState = 'loaded';
    } catch (e) {
      this._errorMessage = e instanceof Error ? e.message : 'Fehler beim Laden';
      this._loadState = 'error';
    }
  }

  private _onContentInput(e: Event): void {
    this._currentContent = (e.target as HTMLTextAreaElement).value;
  }

  private _handleMcpToolToggle(toolName: string): void {
    if (this._selectedMcpTools.includes(toolName)) {
      this._selectedMcpTools = this._selectedMcpTools.filter(t => t !== toolName);
    } else {
      this._selectedMcpTools = [...this._selectedMcpTools, toolName];
    }
  }

  private async _onSave(): Promise<void> {
    if (!this.skillId || this._isSaving) return;
    this._isSaving = true;
    this._errorMessage = '';

    try {
      await teamApi.updateSkill(this.skillId, this._currentContent, this._selectedMcpTools);
      this.dispatchEvent(new CustomEvent('skill-saved', {
        detail: { skillId: this.skillId },
        bubbles: true,
        composed: true,
      }));
      this._close();
    } catch (e) {
      this._errorMessage = e instanceof Error ? e.message : 'Speichern fehlgeschlagen';
    } finally {
      this._isSaving = false;
    }
  }

  render() {
    if (!this.open) return nothing;

    return html`
      <div class="cp-team-edit-modal__overlay" @click=${this._onBackdropClick}>
        <div class="cp-team-edit-modal__dialog">
          <div class="cp-team-edit-modal__header">
            <h2 class="cp-team-edit-modal__heading">
              ${this._skillDetail ? `${this._skillDetail.name} bearbeiten` : 'Skill bearbeiten'}
            </h2>
            <button class="cp-team-edit-modal__close" @click=${this._close}>&times;</button>
          </div>

          <div class="cp-team-edit-modal__body">
            ${this._renderContent()}
          </div>

          ${this._loadState === 'loaded' ? html`
            <div class="cp-team-edit-modal__footer">
              ${this._errorMessage ? html`
                <span class="cp-team-edit-modal__error">${this._errorMessage}</span>
              ` : nothing}
              <button class="cp-team-edit-modal__btn cp-team-edit-modal__btn--cancel" @click=${this._close}>Abbrechen</button>
              <button
                class="cp-team-edit-modal__btn cp-team-edit-modal__btn--save"
                ?disabled=${this._isSaving}
                @click=${this._onSave}
              >${this._isSaving ? 'Speichern...' : 'Speichern'}</button>
            </div>
          ` : nothing}
        </div>
      </div>
    `;
  }

  private _renderContent() {
    switch (this._loadState) {
      case 'idle':
      case 'loading':
        return html`<div class="cp-team-edit-modal__loading"><p>Skill wird geladen...</p></div>`;
      case 'error':
        return html`<div class="cp-team-edit-modal__error-state"><p>${this._errorMessage}</p></div>`;
      case 'loaded':
        return html`
          ${this._renderMcpToolsSection()}
          <textarea
            class="cp-team-edit-modal__textarea"
            .value=${this._currentContent}
            @input=${this._onContentInput}
          ></textarea>
        `;
    }
  }

  private _renderMcpToolsSection() {
    if (this.availableMcpTools.length === 0) {
      return nothing;
    }

    return html`
      <div class="cp-team-edit-modal__mcp-section">
        <h4 class="cp-team-edit-modal__mcp-title">MCP Tools</h4>
        <div class="cp-team-edit-modal__mcp-checkboxes">
          ${this.availableMcpTools.map(tool => html`
            <label class="cp-team-edit-modal__mcp-checkbox">
              <input
                type="checkbox"
                .checked=${this._selectedMcpTools.includes(tool)}
                @change=${() => this._handleMcpToolToggle(tool)}
              />
              <span class="cp-team-edit-modal__mcp-label">${tool}</span>
            </label>
          `)}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-team-edit-modal': CpTeamEditModal;
  }
}
